import { TableCell } from '@/components/Datatable/TableCell';
import { SmallInfoContent } from '@/components/SmallInfoContent';
import { StatusTag } from '@/components/Tag/StatusTag';
import { IDatatableData } from '@/type/listing.types';
import { getFormattedDate, getFormattedTime } from '@/utils/date.utils';
import { FieldValues } from 'react-hook-form';

const formatEndpointSchema = (schema: string) => {
  if (!schema) return null;
  const parts = schema.split(/(:[^/]+)/g);
  return parts.map((part, index) =>
    part.startsWith(':') ? <span className="font-semibold text-primary" key={index}>{part}</span> : part
  );
};

export const EndpointHistoryTableData = <TSchema extends FieldValues>({
  t,
  data,
}: IDatatableData<TSchema>) => {

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {data.map((entry: TSchema) => {
        const licensePlateNumber = entry?.vehiculeCheckResult?.licensePlateNumber;
        const vin = entry?.vehiculeCheckResult?.vin;
        
        const groups = entry?.user?.entityRoles?.filter((role) => role?.group)?.map((role) => role?.group);
        const companies = entry?.user?.entityRoles?.filter((role) => role?.company)?.map((role) => role?.company);
        const branches = entry?.user?.entityRoles?.filter((role) => role?.branch)?.map((role) => role?.branch);

        const groupsExist = !!groups?.length;
        const companiesExist = !!companies?.length;
        const branchesExist = !!branches?.length;

        return (
          <tr
            key={entry?.id}
          >
            <TableCell
              content={
                <div className="flex flex-col">
                  <div className="flex gap-1.5">
                    <div>
                      <i className="-mt-[5.7px] ki-outline ki-calendar text-md" />
                    </div>
                    <div>{getFormattedDate(entry?.createdAt)}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <div>
                      <i className="-mt-[5.7px] ki-outline ki-time text-md" />
                    </div>
                    <div>{getFormattedTime(entry?.createdAt)}</div>
                  </div>
                </div>
              }
            />
            <TableCell
              content={
                <>
                  <div className="flex gap-2">
                    <StatusTag
                      text="123"
                      className="self-start px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                    <div className="mt-[0.5px] font-medium">
                      {licensePlateNumber || '--'}
                    </div>
                  </div>
                  <div className="mt-2 flex gap-2">
                    <StatusTag
                      text="VIN"
                      className="self-start px-1.5 py-[1.5px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs font-normal dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                    <div className="mt-[0.5px] break-all">
                      {vin || '--'}
                    </div>
                  </div>
                </>
              }
            />
            <TableCell
              content={
                <div>
                  <div className="font-medium">
                    {entry?.user?.firstname && entry?.user?.lastname ? `${entry?.user?.firstname} ${entry?.user?.lastname?.toUpperCase()}` : '--'}
                  </div>
                  {(groupsExist || companiesExist || branchesExist) && (
                    <div className="mt-1.5 flex flex-col gap-1">
                      {groupsExist && (
                        <SmallInfoContent
                          label={groups?.length > 1 ? t('common:groups') : t('common:group')}
                          value={groups?.map((group) => group?.name)?.join(', ')}
                        />
                      )}
                      {companiesExist && (
                        <SmallInfoContent
                          label={companies?.length > 1 ? t('common:companies') : t('common:company')}
                          value={companies?.map((company) => company?.name)?.join(', ')}
                        />
                      )}
                      {branchesExist && (
                        <SmallInfoContent
                          label={branches?.length > 1 ? t('common:branches') : t('common:branch')}
                          value={branches?.map((branch) => branch?.name)?.join(', ')}
                        />
                      )}
                    </div>
                  )}
                </div>
              }
            />
            <TableCell
              tdClassName="text-center"
              content={entry?.endpointMethod}
            />
            <TableCell
              tdClassName="break-all"
              content={formatEndpointSchema(entry?.endpointSchema)}
            />
            <TableCell
              tdClassName="break-all"
              content={entry?.endpointWithValues}
            />
          </tr>
        );
      })}
    </>
  );
};
